import { Flex } from '@chakra-ui/react';
import { type ReactNode, useState } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { CustomDashPageSpecificWrappers } from './CustomDashPageSpecificWrappers';
import { ConditionalChartProvider } from './components/ConditionalChartProvider/ConditionalChartProvider';
import { ContentGrid } from './components/ContentGrid/ContentGrid';
import { EditableGrid } from './components/EditableGrid/EditableGrid';
import { useEditMode } from './components/EditModeProvider/EditModeProvider';
import { usePageConfig } from './components/PageConfigProvider/PageConfigProvider';
import type { TabContent } from './components/PageConfigProvider/page-reducer';
import { PageType } from './components/PageConfigProvider/types';
import { SharedReportDataProvider } from './components/SharedReport/SharedReportDataContext';
import { TableSelectionProvider } from './components/TableSelectionProvider/TableSelectionProvider';
import { TextChartContentProvider } from './components/TextChartContentProvider/TextChartContentProvider';
import { TextChartOrchestrator } from './components/TextChartContentProvider/TextChartOrchestrator';
import { SIDE_PANEL_WIDTH } from './constants/panelWidths';
import { usePostingsVolumeChartToggle } from './hooks/usePostingsVolumeChartToggle';
import { useRemovedSavedSetFilter } from './hooks/useRemovedSavedSetFilter';
import { useSentimentItemsOverride } from './hooks/useSentimentItemsOverride';
import { useSetNewFilterPageMap } from './hooks/useSetNewFilterPageMap';
import { useSyncPrimaryEntities } from './hooks/useSyncPrimaryEntities';

interface CustomDashProps {
  tabContent: TabContent;
}

// Skip the provider when no share path is reachable so normal renders avoid
// the per-render prop stringify inside `withShareCapture`.
const ShareCaptureHost = ({ children }: { children: ReactNode }) => {
  const { config } = usePageConfig();
  const isReportPage = config.type === PageType.REPORT;
  const isSharedReportsEnabled = useUnleashFlag(FeatureFlag.SharedReports);
  const isSharedPlotsEnabled = useUnleashFlag(FeatureFlag.SharedPlots);

  const reportCaptureActive = isReportPage && isSharedReportsEnabled;
  if (!reportCaptureActive && !isSharedPlotsEnabled) {
    return children;
  }

  return <SharedReportDataProvider>{children}</SharedReportDataProvider>;
};

export const CustomDash = ({ tabContent }: CustomDashProps) => {
  const { editMode } = useEditMode();

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);

  useSentimentItemsOverride();
  usePostingsVolumeChartToggle();
  useSetNewFilterPageMap();
  useRemovedSavedSetFilter();
  useSyncPrimaryEntities();

  const handleBuilderOpenChange = (isOpen: boolean) => {
    setIsBuilderOpen(isOpen);
    if (isOpen) {
      setIsAIDrawerOpen(false);
    }
  };

  const handleAIDrawerOpenChange = (isOpen: boolean) => {
    setIsAIDrawerOpen(isOpen);
    if (isOpen) {
      setIsBuilderOpen(false);
    }
  };

  const gridContent = editMode ? (
    <EditableGrid
      key={tabContent.id}
      onBuilderOpenChange={handleBuilderOpenChange}
      onAIDrawerOpenChange={handleAIDrawerOpenChange}
    />
  ) : (
    <ContentGrid key={tabContent.id} />
  );

  return (
    <ShareCaptureHost>
      <TableSelectionProvider>
        <TextChartContentProvider>
          <Flex
            flexDirection="column"
            height="100vh"
            position="relative"
            width={(() => {
              if (isBuilderOpen) {
                return `calc(100% - ${SIDE_PANEL_WIDTH})`;
              }
              if (isAIDrawerOpen) {
                return `calc(100% - ${SIDE_PANEL_WIDTH})`;
              }
              return '100%';
            })()}
            transition="width 0.3s ease-in-out"
            padding="0.5rem 1.5rem 1rem 1.5rem"
            backgroundColor="var(--chakra-colors-background-primary)"
            color="var(--chakra-colors-text-primary)"
            overflow={{ base: 'auto', md: 'hidden' }}
          >
            <CustomDashPageSpecificWrappers>
              <TextChartOrchestrator />
              <ConditionalChartProvider>{gridContent}</ConditionalChartProvider>
            </CustomDashPageSpecificWrappers>
          </Flex>
        </TextChartContentProvider>
      </TableSelectionProvider>
    </ShareCaptureHost>
  );
};
